import { useCallback, useMemo } from "react";

import { InAppNotificationType, NotificationProviderType } from "@doitintl/cmp-models";
import Typography from "@mui/material/Typography";

import { FilterTable } from "../../../Components/FilterTable/FilterTable";
import { useUserContext } from "../../../Context/UserContext";
import { toSentenceCase } from "../../../utils/string";
import texts from "./texts";
import { type MyNotificationData, type TableNotification } from "./types";
import { filters as userFilters, headerColumns as userHeaderColumns } from "./userColumns";
import { UserNotificationRow } from "./UserNotificationRow";
import { providerTypeToString, userNotificationsToString } from "./utils";

type Props = {
  data: TableNotification[];
};

export const cyIds = {
  text: {
    title: "userNotificationsTableTitle",
  },
};

export default function UserNotificationsTable({ data }: Props) {
  const { userModel } = useUserContext({ allowNull: false });

  const RowUserWrapper = ({ row }: { row: MyNotificationData }) => <UserNotificationRow row={row} />;

  const buildInAppNotificationTypes = useCallback<() => string>(() => {
    const inAppNotificationTypes: InAppNotificationType[] | undefined =
      userModel.inAppNotificationSettings?.notificationTypes;

    // If the user has not configured any in-app notifications we'll be showing everything.
    // We also want to show "All types" if the user has selected everything.
    if (!inAppNotificationTypes || inAppNotificationTypes.length === Object.keys(InAppNotificationType).length) {
      return texts.table.row.allTypes;
    }

    const sentenceCaseNotifications: string[] | undefined = inAppNotificationTypes?.map((n) => toSentenceCase(n));
    return sentenceCaseNotifications?.join(", ");
  }, [userModel.inAppNotificationSettings?.notificationTypes]);

  const myNotifications = useMemo<MyNotificationData[]>(() => {
    const myNotifications: MyNotificationData[] = [];

    // Add mapped notifications
    data.forEach((row) => {
      myNotifications.push({
        id: row.id,
        providerType: providerTypeToString(row.providerType),
        selectedNotifications: userNotificationsToString(row.selectedNotificationsKeys),
      });
    });

    // Add blank unconfigured email row if data is empty.
    if (!myNotifications.length) {
      myNotifications.push({
        id: "emailDefault",
        providerType: providerTypeToString(NotificationProviderType.EMAIL),
        selectedNotifications: texts.table.row.notSet,
      });
    }

    myNotifications.push({
      id: "in-app",
      providerType: texts.table.row.inApp,
      selectedNotifications: buildInAppNotificationTypes(),
      allowRealTime: userModel.inAppNotificationSettings?.allowRealTime ?? true, // the default behaviour is to allow real-time notifications.
    });

    return myNotifications;
  }, [buildInAppNotificationTypes, data, userModel]);

  return (
    <FilterTable<MyNotificationData>
      toolbarProps={{
        title: (
          <Typography variant="h4" data-cy={cyIds.text.title}>
            {texts.table.title}
          </Typography>
        ),
      }}
      rowComponent={RowUserWrapper}
      headerColumns={userHeaderColumns}
      filterColumns={userFilters}
      tableItems={myNotifications}
      showFilterBar={false}
      fillEmptySpace={false}
    />
  );
}
